import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Download, FileText } from 'lucide-react';

import { useAuth } from '@dashboard/context/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import Link from '@dashboard/components/Link';
import { Badge } from '@dashboard/components/ui/badge';
import { Button } from '@dashboard/components/ui/button';
import { LoaderIcon } from '@dashboard/components/ui/loader-icon';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@dashboard/components/ui/sheet';
import { changeRows } from '@dashboard/scenes/PageEdit/components/AIPanel/aiChangeLocations';
import ChangeRow from '@dashboard/scenes/PageEdit/components/AIPanel/ChangeRow';

import type { AiUsageTurnAttachment, AiUsageTurnRow } from './AiUsageTurnsTable';
import {
  AI_USAGE_PLAN_LABELS,
  AI_USAGE_STATUS_BADGE_VARIANTS,
  AI_USAGE_STATUS_LABELS,
  AI_USAGE_SURFACE_LABELS,
  aiUsagePageHref,
  formatTurnTime,
} from './aiUsageLabels';
import { formatMicros } from './aiUsagePeriod';
import { exportAiChat } from './exportAiChat';

type Props = {
  turn?: AiUsageTurnRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNewer?: () => void;
  onOlder?: () => void;
  loadingOlder?: boolean;
};

const TurnText = ({ text, emptyLabel }: { text?: string; emptyLabel: string }) => (
  <div className="rounded-md bg-muted px-3 py-2.5 text-sm">
    {text ? (
      <p className="whitespace-pre-wrap break-words">{text}</p>
    ) : (
      <p className="text-muted-foreground">{emptyLabel}</p>
    )}
  </div>
);

const TurnChanges = ({ turn }: { turn: AiUsageTurnRow }) => {
  const rows = changeRows(turn.changes, turn.pageType);
  if (rows.length === 0) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-medium">Changes</h3>
      <ul aria-label="Changes" className="flex flex-col rounded-md border px-3 py-2.5">
        {rows.map(row => (
          <ChangeRow key={row.id} row={row} />
        ))}
      </ul>
    </section>
  );
};

const AttachmentRow = ({ attachment }: { attachment: AiUsageTurnAttachment }) => (
  <li className="flex items-center gap-x-3 py-2 first:pt-0 last:pb-0">
    {attachment.previewUrl ? (
      <a
        href={attachment.previewUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 overflow-hidden rounded-md border"
      >
        <img src={attachment.previewUrl} alt={attachment.name} className="size-14 object-cover" />
      </a>
    ) : (
      <div className="flex size-14 shrink-0 items-center justify-center rounded-md border bg-muted">
        <FileText className="size-5 text-muted-foreground" />
      </div>
    )}
    <span className="min-w-0 flex-1 truncate text-sm" title={attachment.name}>
      {attachment.name}
    </span>
    {attachment.downloadUrl ? (
      <Button
        variant="outline"
        size="sm"
        href={attachment.downloadUrl}
        aria-label={`Download ${attachment.name}`}
      >
        <Download />
        Download
      </Button>
    ) : (
      <span className="text-sm text-muted-foreground">File deleted</span>
    )}
  </li>
);

const TurnAttachments = ({ attachments }: { attachments?: AiUsageTurnAttachment[] }) => {
  if (!attachments?.length) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-medium">Attachments</h3>
      <ul aria-label="Attachments" className="flex flex-col divide-y rounded-md border px-3 py-2.5">
        {attachments.map(attachment => (
          <AttachmentRow key={attachment.fileId} attachment={attachment} />
        ))}
      </ul>
    </section>
  );
};

// aria-disabled, not disabled: a disabled button drops focus out of the sheet, and the
// arrow keys only reach the sheet while focus is inside it.
const stepButtonClass = 'aria-disabled:cursor-default aria-disabled:opacity-50';

const isTypingTarget = (target: EventTarget) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));

const AiUsageTurnSheet = ({ turn, open, onOpenChange, onNewer, onOlder, loadingOlder }: Props) => {
  const { token } = useAuth();
  const [exporting, setExporting] = useState(false);

  const exportChat = async () => {
    if (!turn) return;
    setExporting(true);
    try {
      await exportAiChat(token, turn._id, turn.conversationId);
    } catch {
      toast({ title: 'Could not export this chat', type: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (isTypingTarget(event.target)) return;
    if (event.key === 'ArrowLeft' && onNewer) {
      event.preventDefault();
      onNewer();
    } else if (event.key === 'ArrowRight' && onOlder && !loadingOlder) {
      event.preventDefault();
      onOlder();
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-xl" onKeyDown={onKeyDown}>
        {turn && (
          <>
            <div className="space-y-4 border-b px-6 pb-4 pt-6">
              <SheetHeader className="pr-8">
                <SheetTitle>
                  {turn.seller?.displayName || turn.seller?.name || 'Unknown store'}
                </SheetTitle>
                <SheetDescription>{formatTurnTime(turn.createdAt)}</SheetDescription>
              </SheetHeader>

              <div className="flex items-center gap-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  className={stepButtonClass}
                  onClick={onNewer}
                  aria-disabled={!onNewer}
                  title="Newer turn (Left arrow)"
                >
                  <ChevronLeft />
                  Newer
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className={stepButtonClass}
                  onClick={loadingOlder ? undefined : onOlder}
                  aria-disabled={!onOlder || loadingOlder}
                  title="Older turn (Right arrow)"
                >
                  Older
                  {loadingOlder ? <LoaderIcon className="size-3.5" /> : <ChevronRight />}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-auto"
                  onClick={exportChat}
                  disabled={exporting}
                >
                  {exporting ? <LoaderIcon className="size-3.5" /> : <Download />}
                  Export chat
                </Button>
              </div>
            </div>

            <div key={turn._id} className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-6">
              <dl className="grid grid-cols-[auto_1fr] items-center gap-x-6 gap-y-2 text-sm">
                <dt className="text-gray-500 dark:text-muted-foreground">User</dt>
                <dd className="break-all">{turn.user?.email || '-'}</dd>
                <dt className="text-gray-500 dark:text-muted-foreground">Surface</dt>
                <dd>{AI_USAGE_SURFACE_LABELS[turn.surface] ?? turn.surface}</dd>
                <dt className="text-gray-500 dark:text-muted-foreground">Plan</dt>
                <dd>{AI_USAGE_PLAN_LABELS[turn.plan] ?? turn.plan}</dd>
                <dt className="text-gray-500 dark:text-muted-foreground">Status</dt>
                <dd>
                  <Badge variant={AI_USAGE_STATUS_BADGE_VARIANTS[turn.status] ?? 'outline'}>
                    {AI_USAGE_STATUS_LABELS[turn.status] ?? turn.status}
                  </Badge>
                </dd>
                <dt className="text-gray-500 dark:text-muted-foreground">Estimated cost</dt>
                <dd>{formatMicros(turn.costMicros)}</dd>
                <dt className="text-gray-500 dark:text-muted-foreground">Tokens</dt>
                <dd>{turn.totalTokens.toLocaleString()}</dd>
                {turn.page && (
                  <>
                    <dt className="text-gray-500 dark:text-muted-foreground">Page</dt>
                    <dd>
                      <Link
                        to={aiUsagePageHref(turn.page)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View page
                      </Link>
                    </dd>
                  </>
                )}
                {turn.offTopic && (
                  <>
                    <dt className="text-gray-500 dark:text-muted-foreground">Topic</dt>
                    <dd className="flex items-center gap-x-2">
                      <Badge variant="warning">Off-topic</Badge>
                      {turn.topic && <span>{turn.topic}</span>}
                    </dd>
                  </>
                )}
              </dl>

              <section className="space-y-2">
                <h3 className="text-sm font-medium">Prompt</h3>
                <TurnText text={turn.prompt} emptyLabel="No prompt saved" />
              </section>

              <TurnAttachments attachments={turn.attachments} />

              <section className="space-y-2">
                <h3 className="text-sm font-medium">Response</h3>
                <TurnText text={turn.reply} emptyLabel="No text reply" />
                {turn.error && <p className="text-sm text-destructive">{turn.error}</p>}
              </section>

              <TurnChanges turn={turn} />

              {turn.tools?.length > 0 && (
                <section className="space-y-2">
                  <h3 className="text-sm font-medium">Tools</h3>
                  <div className="flex flex-wrap gap-1.5">
                    {turn.tools.map((tool, index) => (
                      <Badge
                        key={index}
                        variant={tool.ok === false ? 'destructive' : 'outline'}
                        className="font-mono"
                      >
                        {tool.name}
                      </Badge>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default AiUsageTurnSheet;
